import React, { useState } from 'react';
import {
  Coins, Languages, MapPin, Phone, Save, ShieldCheck, UserRound,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import { XomicsSelect } from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { tx, useXomicsI18n } from './i18n';
import { roleLabel } from './labels';
import { useExchangeRates } from './viewMoney';

// Editing your own details. Before this existed there was no way to set a phone
// number at all, which meant the SMS column on the notification preferences
// screen could never be switched on by anyone.
export function ProfileSettings() {
  const { user, updateProfile } = useXomicsAuth();
  const { languages } = useXomicsI18n();
  const rates = useExchangeRates();
  const currencies = (rates.data?.currencies ?? []).filter((currency) => currency.isActive || currency.code === user.preferredCurrency);
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    phoneNumber: user.phoneNumber || '',
    preferredLanguage: user.preferredLanguage || 'en',
    preferredCurrency: user.preferredCurrency || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  function set(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setSaved(false);
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    setSaved(false);
    setSaving(true);
    try {
      await updateProfile({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        // Cleared with null rather than an empty string: the server treats null
        // as "remove this" and rejects '' as a malformed number.
        phoneNumber: form.phoneNumber.trim() === '' ? null : form.phoneNumber.trim(),
        preferredLanguage: form.preferredLanguage,
        // Sent only once the currencies are read, so a profile saved without them keeps its choice.
        ...(rates.data ? { preferredCurrency: form.preferredCurrency === '' ? null : form.preferredCurrency } : {}),
      });
      setSaved(true);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  }

  const fullName = `${user.firstName} ${user.lastName}`.trim();
  const userInitials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();

  return (
    <section className="xo-profile-card">
      <header className="xo-profile-identity">
        <span className="xo-profile-avatar" aria-hidden="true">
          {userInitials || <UserRound />}
        </span>
        <div>
          <p className="xo-eyebrow">{tx("ui.profile.profile_ff4fc027")}</p>
          <h2>{fullName}</h2>
          <span>{user.email}</span>
        </div>
        <div className="xo-profile-context">
          <span><ShieldCheck aria-hidden="true" />{roleLabel(user.roleCode)}</span>
          <span><MapPin aria-hidden="true" />{user.location.name}</span>
        </div>
      </header>

      <form onSubmit={submit} className="xo-profile-form">
        <header>
          <div>
            <h3>{tx("ui.profile.your_details_a6252b66")}</h3>
            <p>{tx("ui.profile.signed_in_as_email_contact_an_administrator_to_change__b63b41aa", { email: user.email })}</p>
          </div>
        </header>
        <div className="xo-profile-fields">
          <Field id="profile-first-name" label={tx("ui.profile.first_name_7e568a90")}>
            <div className="xo-profile-control-icon">
              <UserRound aria-hidden="true" />
              <input
                type="text"
                {...fieldAria('profile-first-name')}
                value={form.firstName}
                disabled={saving}
                onChange={(event) => set('firstName', event.target.value)}
              />
            </div>
          </Field>

          <Field id="profile-last-name" label={tx("ui.profile.last_name_adec36a8")}>
            <div className="xo-profile-control-icon">
              <UserRound aria-hidden="true" />
              <input
                type="text"
                {...fieldAria('profile-last-name')}
                value={form.lastName}
                disabled={saving}
                onChange={(event) => set('lastName', event.target.value)}
              />
            </div>
          </Field>

          <Field
            id="profile-phone"
            label={tx("ui.profile.phone_number_8961d3bf")}
            hint={tx("ui.profile.international_format_for_example_46701234567_required__28189100")}
          >
            <div className="xo-profile-control-icon">
              <Phone aria-hidden="true" />
              <input
                {...fieldAria('profile-phone', { hint: 'x' })}
                type="tel"
                value={form.phoneNumber}
                disabled={saving}
                placeholder="+46701234567"
                onChange={(event) => set('phoneNumber', event.target.value)}
              />
            </div>
          </Field>

          <Field id="profile-language" label={tx("ui.profile.preferred_language_1c7b0d77")}>
            <div className="xo-profile-control-icon">
              <Languages aria-hidden="true" />
              <XomicsSelect
                {...fieldAria('profile-language')}
                value={form.preferredLanguage}
                disabled={saving}
                onChange={(event) => set('preferredLanguage', event.target.value)}
              >
                {languages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.nativeName || language.name} · {language.code.toUpperCase()}
                  </option>
                ))}
              </XomicsSelect>
            </div>
          </Field>

          <Field id="profile-currency" label={tx('ui.profile.view_currency')} hint={tx('ui.profile.view_currency_hint')}>
            <div className="xo-profile-control-icon">
              <Coins aria-hidden="true" />
              <XomicsSelect
                {...fieldAria('profile-currency', { hint: 'x' })}
                value={form.preferredCurrency}
                disabled={saving || !rates.data}
                onChange={(event) => set('preferredCurrency', event.target.value)}
              >
                <option value="">{tx('ui.profile.view_currency_none')}</option>
                {currencies.map((currency) => (
                  <option key={currency.code} value={currency.code}>{currency.code} · {currency.name}</option>
                ))}
              </XomicsSelect>
            </div>
          </Field>
        </div>

        <footer>
          <div>
            {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
            {saved && <p className="xo-profile-saved" role="status">{tx("ui.profile.saved_9d4af690")}</p>}
          </div>
          <button type="submit" className="xo-btn" disabled={saving}>
            <Save aria-hidden="true" />
            {saving ? tx("ui.profile.saving_56a2285c") : tx("ui.profile.save_details_4706583d")}
          </button>
        </footer>
      </form>
    </section>
  );
}
